import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { DealItem, DealItemInsert } from "@/types/deal";

export function useDealItems(dealId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["deal-items", dealId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!dealId) return [];
      const { data, error } = await supabase
        .from("deal_items")
        .select("*")
        .eq("deal_id", dealId)
        .order("sort_order");
      if (error) throw error;
      return data as DealItem[];
    },
    enabled: !!dealId,
  });

  const addItem = useMutation({
    mutationFn: async (item: DealItemInsert) => {
      const { error } = await supabase.from("deal_items").insert(item);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const updateItem = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DealItem> & { id: string }) => {
      const { error } = await supabase.from("deal_items").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("deal_items").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  return { ...query, items: query.data ?? [], addItem, updateItem, deleteItem };
}
